// Valid Twirp error types. Most error types are equivalent to gRPC status codes
// and follow the same semantics.
// From twitchtv/twirp@v8.1.0+incompatible/errors.go
export enum TwirpErrorCode {
  Oops = 'oops',
  Canceled = 'canceled',
  Unknown = 'unknown',
  InvalidArgument = 'invalid_argument',
  Malformed = 'malformed',
  DeadlineExceeded = 'deadline_exceeded',
  NotFound = 'not_found',
  BadRoute = 'bad_route',
  AlreadyExists = 'already_exists',
  PermissionDenied = 'permission_denied',
  Unauthenticated = 'unauthenticated',
  ResourceExhausted = 'resource_exhausted',
  FailedPrecondition = 'failed_precondition',
  Aborted = 'aborted',
  OutOfRange = 'out_of_range',
  Unimplemented = 'unimplemented',
  Internal = 'internal',
  Unavailable = 'unavailable',
  DataLoss = 'data_loss',
  NoError = 'ok',
  BadGateway = 'bad_gateway', // this case will be needed to be treated in the HTTP Header level because twirp won't throw it
  UnreachableOrigin = 'unreachable_origin', // this case will be needed to be treated in the HTTP Header level because twirp won't throw it
}

export const AllHTTPStatus = [
  200, // Ok
  400, // BadRequest
  401, // Unauthorized
  403, // Conflict
  404, // Not Found
  408, // Request Timeout
  409, // Conflict
  412, // Precondition Failed
  429, // Too Many Requests
  500, // Internal Server Error
  501, // Not Implemented
  502, // Bad Gateway
  503, // Service Unavailable
  523, // Unreachable Origin
  0, // Oops
] as const;
type HTTPStatusTuple = typeof AllHTTPStatus;
export type HTTPStatus = HTTPStatusTuple[number];

// this is not currently being used but it is a sample on
// how to test possible values through the tuple
export function isHTTPStatus(value: number): value is HTTPStatus {
  return AllHTTPStatus.includes(value as HTTPStatus);
}

export type TwirpErrorResponse = {
  code?: TwirpErrorCode;
  message?: string;
  status?: HTTPStatus;
  fatal?: boolean;
};

export const getHTTPResponse = (response: TwirpErrorResponse): TwirpErrorResponse => {
  const { code, message: originalMessage } = response;
  let status: HTTPStatus,
    message = originalMessage;
  if (!code) {
    return { fatal: true, message: 'invalid usage of HTTP Response Handler: missing twirp code' };
  }
  switch (code) {
    case TwirpErrorCode.Oops:
      status = 0;
      message = 'Oops, something went wrong.';
      break;
    case TwirpErrorCode.NoError:
      status = 200;
      break;
    case TwirpErrorCode.InvalidArgument:
    case TwirpErrorCode.Malformed:
    case TwirpErrorCode.OutOfRange:
      message = 'Looks like part or all of your request is not accurate';
      status = 400;
      break;
    case TwirpErrorCode.Unauthenticated:
      status = 401;
      break;
    case TwirpErrorCode.PermissionDenied:
      status = 403;
      break;
    case TwirpErrorCode.BadRoute:
    case TwirpErrorCode.NotFound:
      status = 404;
      break;
    case TwirpErrorCode.Canceled:
    case TwirpErrorCode.DeadlineExceeded:
      status = 408;
      break;
    case TwirpErrorCode.Aborted:
    case TwirpErrorCode.AlreadyExists:
      status = 409;
      break;
    case TwirpErrorCode.FailedPrecondition:
      status = 412;
      break;
    case TwirpErrorCode.ResourceExhausted:
      status = 429;
      break;
    case TwirpErrorCode.DataLoss:
    case TwirpErrorCode.Internal:
    case TwirpErrorCode.Unknown:
      message = 'Internal server error. Please refresh this page or try again later';
      status = 500;
      break;
    case TwirpErrorCode.Unimplemented:
      status = 501;
      break;
    case TwirpErrorCode.BadGateway: // this is not from twirp, but could happen between UI and Network
      message = "Somewhere along the way, we are losing connection. Let's try again later, please";
      status = 502;
      break;
    case TwirpErrorCode.Unavailable:
      message = 'Looks like some of our resources are currently unavailable';
      status = 503;
      break;
    case TwirpErrorCode.UnreachableOrigin:
      message = 'We are currently unable to reach out our origin. Please, try again later';
      status = 523;
      break;
    default:
      throw new Error('undetected HTTP Code');
  }
  return { ...response, status, message };
};

// @see https://grpc.github.io/grpc/core/md_doc_statuscodes.html
export enum GrpcStatusCode {
  OK = 0,
  CANCELLED = 1,
  UNKNOWN = 2,
  INVALID_ARGUMENT = 3,
  DEADLINE_EXCEEDED = 4,
  NOT_FOUND = 5,
  ALREADY_EXISTS = 6,
  PERMISSION_DENIED = 7,
  RESOURCE_EXHAUSTED = 8,
  FAILED_PRECONDITION = 9,
  ABORTED = 10,
  OUT_OF_RANGE = 11,
  UNIMPLEMENTED = 12,
  INTERNAL = 13,
  UNAVAILABLE = 14,
  DATA_LOSS = 15,
  UNAUTHENTICATED = 16,
}

export type GrpcGatewayErrorResponse = {
  code: GrpcStatusCode;
  message: string;
  details?: string[];
};

export const isGrpcGatewayErrorResponse = (err: unknown): err is GrpcGatewayErrorResponse => {
  if (typeof err === 'object' && err !== null) {
    const { code, message } = err as GrpcGatewayErrorResponse;
    return typeof code === 'number' && typeof message === 'string';
  }
  return false;
};
